/**
 * AL SULTAN — Advanced Motion Controller
 * Scroll-driven parallax, cinematic card reveals, text choreography.
 * Progressive enhancement: degrades gracefully on older browsers.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

  /* ─────────────────────────────────────────────────────────────
     1. HERO PARALLAX
     Image moves at 0.28x scroll — real sense of depth.
     Text moves at 0.52x — separates from image.
     Overlay thickens as you scroll away.
  ───────────────────────────────────────────────────────────── */
  function initHeroParallax() {
    if (REDUCED) return;
    var hero = document.querySelector('.luxury-hero');
    if (!hero) return;
    var media = hero.querySelector('.luxury-hero__media');
    var content = hero.querySelector('.luxury-hero__content');
    var heroH = 0;
    var rafPending = false;

    function measure() {
      heroH = hero.offsetHeight;
    }

    function tick() {
      rafPending = false;
      var scrollY = window.scrollY;
      if (!heroH || scrollY > heroH) return;

      // Fractional pixel paralax — image moves slower than page
      hero.style.setProperty('--als-hero-img-y', (scrollY * 0.28).toFixed(1) + 'px');
      // Text drifts upward faster than image, creating a 3-D separation
      hero.style.setProperty('--als-hero-txt-y', (scrollY * 0.52).toFixed(1) + 'px');
      // Vignette intensifies (capped at 0.42 opacity)
      var ratio = Math.min(scrollY / heroH * 1.6, 0.42).toFixed(3);
      hero.style.setProperty('--als-hero-scroll-veil', ratio);
    }

    function onScroll() {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(tick);
      }
    }

    // Phase 7: the height arrives from a ResizeObserver after the browser's own layout,
    // instead of a synchronous read during start-up.
    if (window.ResizeObserver) {
      new ResizeObserver(function (entries) {
        heroH = entries[0].target.offsetHeight;
      }).observe(hero);
    } else {
      measure();
      window.addEventListener('resize', measure, { passive: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────
     2. PRODUCT CARD CINEMATIC REVEAL
     Two-phase reveal as a card enters the viewport:
       Phase 1 (0–650ms)  : image clips in from top-to-bottom
       Phase 2 (220–720ms): card info rises up
       Phase 3 (400–850ms): gold accent draws itself
     Cards already in the viewport on page-load reveal with a
     short no-delay animation so there's no flash of empty space.
     Off-screen cards animate in as the user scrolls.
  ───────────────────────────────────────────────────────────── */
  function initCardReveals() {
    if (REDUCED) {
      document.querySelectorAll('.product-card-wrapper').forEach(function (c) {
        c.classList.add('als-card-init', 'als-card-revealed');
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        card.classList.add('als-card-revealed');
        io.unobserve(card);
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -30px 0px' });

    var vw = document.documentElement.clientWidth;
    var vh = window.innerHeight;
    var columns = vw >= 990 ? 4 : vw >= 750 ? 3 : 2;

    // Phase 7: read every card's position before writing any class or style, so the page is
    // laid out once instead of once per card (on collections that was ~30 forced layouts).
    var cards = Array.prototype.slice.call(document.querySelectorAll('.product-card-wrapper'));
    var visible = cards.map(function (card) {
      var rect = card.getBoundingClientRect();
      return rect.top < vh && rect.bottom > 0;
    });

    cards.forEach(function (card, i) {
      var col = i % columns;
      var inViewport = visible[i];

      card.classList.add('als-card-init');

      if (inViewport) {
        // Already visible — reveal quickly with a tiny stagger (no long delay)
        card.style.setProperty('--als-reveal-delay', (col * 0.06) + 's');
        // Small timeout so CSS transition is registered before the class change
        setTimeout(function () { card.classList.add('als-card-revealed'); }, 50 + col * 40);
      } else {
        card.style.setProperty('--als-reveal-delay', (col * 0.09) + 's');
        io.observe(card);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     3. SECTION HEADING WORD CHOREOGRAPHY
     Each word in a key heading rises from below a clip mask
     in staggered sequence — editorial, cinematic reveal.
     Accessibility: original text set on aria-label.
  ───────────────────────────────────────────────────────────── */
  var HEADING_SELECTORS = [
    '.why-al-sultan__heading',
    '.cg__heading',
    '.ts__heading',
    '.sin__heading',
    '.pc__heading',
    '.vss__heading',
    '.newsletter__heading',
  ];

  function splitHeadingWords(el) {
    if (el.dataset.alsSplit) return; // already done
    el.dataset.alsSplit = '1';

    // innerText is layout-aware: <br> → \n, so it splits correctly
    var original = (el.innerText || el.textContent).trim();
    if (!original) return;

    // Preserve accessibility
    el.setAttribute('aria-label', original);

    var words = original.split(/\s+/).filter(Boolean);
    el.innerHTML = words.map(function (word, i) {
      return '<span class="als-word" style="--als-word-i:' + i + '" aria-hidden="true">' + word + '</span>';
    }).join(' ');

    el.classList.add('als-heading-split');
  }

  function initHeadingReveals() {
    if (REDUCED) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        splitHeadingWords(el);
        // Tiny delay so the DOM updates before the animation fires
        requestAnimationFrame(function () {
          el.classList.add('als-heading-revealed');
        });
        io.unobserve(el);
      });
    }, { threshold: 0.4, rootMargin: '0px 0px -30px 0px' });

    HEADING_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        io.observe(el);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     4. SECTION ENTRY CHOREOGRAPHY
     Generic section blocks get a sophisticated depth-entry:
     scale(0.97) + translateY(20px) → scale(1) + translateY(0)
     Staggered children within each block.
  ───────────────────────────────────────────────────────────── */
  function initSectionReveals() {
    if (REDUCED) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('als-section-revealed');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    var SECTION_SELECTORS = [
      '.why-al-sultan__item',   // Why Al Sultan trust items
      '.sin__quadrant',         // Sultan In Numbers stat boxes
      '.cg__panel',             // Collection gateway panels
      '.ts__story',             // Testimonials story cards
      '.vss__header',           // Video section header block
    ];

    SECTION_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el, i) {
        el.style.setProperty('--als-entry-delay', (i * 0.08) + 's');
        el.classList.add('als-section-init');
        io.observe(el);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     5. TOUCH FEEDBACK — Mobile product cards
     Warm inner-glow pressed state on touchstart.
  ───────────────────────────────────────────────────────────── */
  function initTouchFeedback() {
    if (!IS_TOUCH) return;

    document.addEventListener('touchstart', function (e) {
      var card = e.target.closest('.product-card-wrapper');
      if (!card) return;
      card.classList.add('als-card-pressed');
    }, { passive: true });

    document.addEventListener('touchend', function (e) {
      var card = e.target.closest('.product-card-wrapper');
      if (!card) return;
      setTimeout(function () {
        card.classList.remove('als-card-pressed');
      }, 200);
    }, { passive: true });

    document.addEventListener('touchcancel', function (e) {
      var card = e.target.closest('.product-card-wrapper');
      if (card) card.classList.remove('als-card-pressed');
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────
     6. SULTAN IN NUMBERS — Elastic number counter
     Numbers count from 0 to their target value with an
     elastic deceleration curve (overshoots then settles).
  ───────────────────────────────────────────────────────────── */
  function elasticCount(el, target, suffix, duration) {
    var start = null;
    var isDecimal = target % 1 !== 0;
    var decimals = isDecimal ? (target.toString().split('.')[1] || '').length : 0;

    function easeOutElastic(t) {
      var c4 = (2 * Math.PI) / 3.5;
      return t === 0 ? 0 : t === 1 ? 1
        : Math.pow(2, -9 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    }

    function step(ts) {
      if (!start) start = ts;
      var progress = Math.min((ts - start) / duration, 1);
      var value = easeOutElastic(progress) * target;
      var display = isDecimal ? value.toFixed(decimals) : Math.round(value).toLocaleString();
      el.textContent = display;
      if (progress < 1) requestAnimationFrame(step);
      else el.textContent = isDecimal ? target.toFixed(decimals) : target.toLocaleString();
    }

    requestAnimationFrame(step);
  }

  function initNumberCounters() {
    if (REDUCED) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // .sin__quadrant contains .sin__number-value for the number
        var numEl = entry.target.querySelector('.sin__number-value');
        if (!numEl || numEl.dataset.alsCounted) return;
        numEl.dataset.alsCounted = '1';

        var raw = (numEl.dataset.value || numEl.textContent).replace(/[^0-9.]/g, '');
        var target = parseFloat(raw);
        var suffix = (numEl.dataset.suffix || '');
        if (!isNaN(target)) {
          numEl.textContent = '0';
          elasticCount(numEl, target, suffix, 1600);
        }
        io.unobserve(entry.target);
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.sin__quadrant').forEach(function (el) {
      io.observe(el);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     7. COLLECTION GATEWAY — In-panel parallax
     The image inside each panel moves at 0.7x the scroll rate
     of the panel itself, creating genuine depth as users scroll
     through the section on mobile.
  ───────────────────────────────────────────────────────────── */
  function initCGParallax() {
    if (REDUCED) return;
    var panels = null;
    var rafPending = false;

    function tick() {
      rafPending = false;
      if (!panels || !panels.length) return;
      // Phase 7: measure every panel first, then write; interleaving the two forced a fresh
      // layout per panel on every scroll frame.
      var rects = panels.map(function (panel) {
        return panel.getBoundingClientRect();
      });

      panels.forEach(function (panel, i) {
        var rect = rects[i];
        // Only process panels near the viewport (±200px)
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;

        // How far the centre of the panel is from the centre of the viewport
        var panelCenterY = rect.top + rect.height / 2;
        var vpCenterY = window.innerHeight / 2;
        var offset = (panelCenterY - vpCenterY) * 0.18;

        panel.style.setProperty('--cg-parallax-y', offset.toFixed(1) + 'px');
      });
    }

    function onScroll() {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(tick);
      }
    }

    function setup() {
      panels = Array.from(document.querySelectorAll('.cg__panel'));
      if (panels.length) {
        window.addEventListener('scroll', onScroll, { passive: true });
        tick();
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup);
    } else {
      setup();
    }
  }

  /* ─────────────────────────────────────────────────────────────
     8. TESTIMONIAL HIGHLIGHT — Active slide gets a
     momentary typographic emphasis on reveal.
  ───────────────────────────────────────────────────────────── */
  function initTestimonialMotion() {
    if (REDUCED) return;

    var slider = document.querySelector('testimonials-slider, .testimonials-slider');
    if (!slider) return;

    // Watch for slide changes via attribute mutation
    var observer = new MutationObserver(function () {
      var activeSlide = slider.querySelector('.ts__slide.is-active, .testimonials-slider__slide.is-active');
      if (!activeSlide) return;
      activeSlide.classList.remove('als-ts-enter');
      void activeSlide.offsetWidth;
      activeSlide.classList.add('als-ts-enter');
    });

    observer.observe(slider, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* ─────────────────────────────────────────────────────────────
     BOOT
  ───────────────────────────────────────────────────────────── */
  function boot() {
    initHeroParallax();
    initCGParallax();
    initCardReveals();
    initHeadingReveals();
    initSectionReveals();
    initTouchFeedback();
    initNumberCounters();
    initTestimonialMotion();

    document.documentElement.classList.add('als-motion-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    // Re-run on theme editor section loads
    initCardReveals();
    initHeadingReveals();
    initSectionReveals();
    initNumberCounters();
  });
})();
